import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Plus, Trash2, X } from 'lucide-react';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';

const quotationItemCatalogLabel = (item = {}, options = []) => {
  const sku = String(item.product_sku || '').trim();
  if (sku) {
    const match = (options || []).find((option) => String(option.value || '').trim() === sku);
    if (match?.name) return match.name;
    if (match?.label) return match.label;
    return String(item.item_name || '').trim() || sku;
  }
  return String(item.item_name || '').trim();
};

const quotationItemOptionLabel = (option = {}) =>
  String(option.name || option.label || option.value || '').trim();

const quotationItemRowKey = (item = {}, index = 0) =>
  String(item.row_key || (item.id != null ? `qi-${item.id}` : `idx-${index}`));

export const QuotationsForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  onAddItem,
  onRemoveItem,
  onDuplicateItem,
  onItemChange,
  onEmit,
  formError = '',
  fieldErrors = {},
  getUnitPriceWarning = null,
  organizationOptions = [],
  contactOptions = [],
  userOptions = [],
  itemDescriptionOptions = [],
  itemFormatOptions = [],
  paymentTermsOptions = [],
  channelOptions = [],
  itemColorOptions = [],
  buildItemSku = () => '',
  upsellingOptions = [],
  upsellingCategoryOptions = [],
  onAddUpsellingItem,
  onCreateUpsellingProduct,
  onAddOrganization,
  onAddContact,
  pdfProductImageSizeOptions = [],
  formSessionKey = 'new',
  draftAutosaveEnabled = true,
  onDraftAutosaveEnabledChange,
  draftAutosaveStatus = 'idle',
  draftAutosaveMessage = '',
  showDraftAutosaveControls = false,
}) => {
  const ui = useUIColors();
  const colors = {
    overlay: ui.overlay,
    panel: ui.panel,
    panelBorder: ui.panelBorder,
    title: ui.title,
    text: ui.text,
    muted: ui.muted,
    inputBg: ui.inputBg,
    inputBorder: ui.inputBorder,
    inputText: ui.inputText,
    sectionBg: ui.sectionBg,
    sectionBorder: ui.sectionBorder,
    danger: ui.danger,
  };

  const baseInputStyle = {
    width: '100%',
    height: '34px',
    borderRadius: '8px',
    border: `1px solid ${colors.inputBorder}`,
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    padding: '0 10px',
    fontSize: '13px',
    boxSizing: 'border-box',
  };
  const baseTextareaStyle = {
    ...baseInputStyle,
    minHeight: '110px',
    height: '110px',
    padding: '8px 10px',
    resize: 'vertical',
    fontFamily: 'inherit',
  };

  const quotationItems = formData.quotation_items || [];
  const availableOrganizations = organizationOptions.filter((option) => option.value !== '');
  const selectedOrganization = availableOrganizations.find(
    (option) => String(option.value) === String(formData.organization_id || '')
  );
  const [organizationSearch, setOrganizationSearch] = useState(selectedOrganization?.label || '');
  const [showOrgOptions, setShowOrgOptions] = useState(false);
  const [itemDescriptionSearch, setItemDescriptionSearch] = useState({});
  const [openDescriptionKey, setOpenDescriptionKey] = useState(null);
  const [itemColorSearch, setItemColorSearch] = useState({});
  const [openColorKey, setOpenColorKey] = useState(null);
  const [selectedUpsellingCode, setSelectedUpsellingCode] = useState('');
  const [showUpsellingModal, setShowUpsellingModal] = useState(false);
  const [showOtherTermsModal, setShowOtherTermsModal] = useState(false);
  const [newUpsellingName, setNewUpsellingName] = useState('');
  const [newUpsellingPrice, setNewUpsellingPrice] = useState('');
  const [upsellingCategoryMode, setUpsellingCategoryMode] = useState('auto');
  const [manualUpsellingCategoryCode, setManualUpsellingCategoryCode] = useState('');
  const [upsellingCreateLoading, setUpsellingCreateLoading] = useState(false);
  const [upsellingCreateError, setUpsellingCreateError] = useState('');

  useEffect(() => {
    if (!showUpsellingModal) return;
    setNewUpsellingName('');
    setNewUpsellingPrice('');
    setUpsellingCategoryMode('auto');
    setManualUpsellingCategoryCode('');
    setUpsellingCreateError('');
  }, [showUpsellingModal]);

  useEffect(() => {
    setOrganizationSearch(selectedOrganization?.label || '');
  }, [selectedOrganization?.label]);

  useEffect(() => {
    if (!isOpen) {
      setItemDescriptionSearch({});
      setItemColorSearch({});
      setOpenDescriptionKey(null);
      setOpenColorKey(null);
      return;
    }
    const items = formData.quotation_items || [];
    const descNext = {};
    const colorNext = {};
    items.forEach((item, index) => {
      const rowKey = quotationItemRowKey(item, index);
      descNext[rowKey] = quotationItemCatalogLabel(item, itemDescriptionOptions);
      colorNext[rowKey] = item.color || '';
    });
    setItemDescriptionSearch(descNext);
    setItemColorSearch(colorNext);
  }, [isOpen, formSessionKey]);
  useEffect(() => {
    if (!upsellingOptions.some((option) => option.value === selectedUpsellingCode)) {
      setSelectedUpsellingCode('');
    }
  }, [upsellingOptions, selectedUpsellingCode]);

  const filteredOrganizations = useMemo(() => {
    const query = organizationSearch.trim().toLowerCase();
    const list = availableOrganizations.filter((option) => option.value);
    if (!query) return list.slice(0, 12);
    return list
      .filter((option) => {
        const label = String(option.label || '').toLowerCase();
        const rut = String(option.rut || '').toLowerCase();
        return label.includes(query) || rut.includes(query);
      })
      .slice(0, 12);
  }, [availableOrganizations, organizationSearch]);

  const computedTotal = useMemo(
    () =>
      quotationItems.reduce((acc, item) => {
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unit_price || 0);
        const discount = Number(item.discount_percent || 0);
        const lineTotal = quantity * unitPrice * (1 - Math.max(Math.min(discount, 100), 0) / 100);
        return acc + lineTotal;
      }, 0),
    [quotationItems]
  );

  const netTotal = Math.round(Number(computedTotal || 0));
  const ivaTotal = Math.round(netTotal * 0.19);
  const grossTotal = netTotal + ivaTotal;

  const formatMoneyClp = (value) =>
    Number(value || 0).toLocaleString('es-CL', {
      style: 'currency',
      currency: formData.currency || 'CLP',
      maximumFractionDigits: 0,
    });

  const fieldErrorStyle = { color: colors.danger, fontSize: '11px', marginTop: '4px', lineHeight: 1.35 };
  const fieldWarningStyle = { color: '#b45309', fontSize: '11px', marginTop: '4px', lineHeight: 1.35 };
  const isFieldWarningMessage = (message) => {
    const text = String(message || '');
    return (
      text.startsWith('Advertencia') ||
      text.includes('en 0') ||
      text.includes('bajo el mínimo') ||
      text.includes('sobre el máximo') ||
      text.includes('fuera del rango')
    );
  };
  const renderFieldError = (message) => {
    if (!message) return null;
    return (
      <div style={isFieldWarningMessage(message) ? fieldWarningStyle : fieldErrorStyle}>
        {message}
      </div>
    );
  };
  const livePriceRangeCount = quotationItems.reduce((count, item) => {
    if (typeof getUnitPriceWarning !== 'function') return count;
    return getUnitPriceWarning(item) ? count + 1 : count;
  }, 0);

  return (
    isOpen ? (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: colors.overlay,
          zIndex: 10000,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <div
          style={{
            width: '1400px',
            maxWidth: '96vw',
            height: '100vh',
            backgroundColor: colors.panel,
            borderLeft: `1px solid ${colors.panelBorder}`,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              padding: '16px 18px',
              borderBottom: `1px solid ${colors.panelBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{ margin: 0, color: colors.title, fontSize: '16px' }}>
                {isEditing ? 'Editar Cotización' : 'Agregar Cotización'}
              </h3>
              <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: '12px' }}>
                Crea la cotización y sus ítems en un solo formulario
              </p>
              {showDraftAutosaveControls ? (
                <div
                  style={{
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px 14px',
                  }}
                >
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: colors.text,
                      fontSize: '12px',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(draftAutosaveEnabled)}
                      disabled={isSaving}
                      onChange={(event) =>
                        onDraftAutosaveEnabledChange?.(event.target.checked)
                      }
                      style={{ margin: 0 }}
                    />
                    Autoguardar borrador
                  </label>
                  {draftAutosaveMessage ? (
                    <span
                      style={{
                        color:
                          draftAutosaveStatus === 'error'
                            ? colors.danger
                            : draftAutosaveStatus === 'saved'
                              ? '#0f766e'
                              : colors.muted,
                        fontSize: '11px',
                      }}
                    >
                      {draftAutosaveMessage}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              style={{ background: 'transparent', border: 'none', color: colors.muted, cursor: 'pointer', flexShrink: 0 }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
            <div
              style={{
                background: colors.sectionBg,
                border: `1px solid ${colors.sectionBorder}`,
                borderRadius: '10px',
                padding: '12px',
                marginBottom: '12px',
              }}
            >
              {(() => {
                const sectionTitle = (title) => (
                  <h4
                    style={{
                      margin: '0 0 8px',
                      color: colors.text,
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {title}
                  </h4>
                );
                const rowStyle = {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: '10px',
                  marginBottom: '14px',
                };
                const channelValue = String(formData.channel || '').trim();
                const tenderEditable = Boolean(channelValue) && channelValue !== 'Directo';
                const deliveryPlaceEditable =
                  String(formData.delivery_method || 'Despacho') === 'Despacho';
                const selectedOrgRut = selectedOrganization?.rut || '';

                return (
                  <>
                    <div>
                      {sectionTitle('Identificación')}
                      <div style={rowStyle}>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Código Cotización
                          <input
                            style={baseInputStyle}
                            value={
                              formData.quotation_code === 'Se asigna automáticamente'
                                ? ''
                                : formData.quotation_code || ''
                            }
                            onChange={(event) => onChange('quotation_code', event.target.value)}
                            placeholder={isEditing ? 'Ej. Q2500042' : 'Se asigna automáticamente'}
                          />
                          {!isEditing ? (
                            <span style={{ display: 'block', marginTop: '4px', color: colors.muted, fontSize: '11px' }}>
                              Déjalo vacío para generar el código automáticamente.
                            </span>
                          ) : null}
                          {renderFieldError(fieldErrors.quotation_code)}
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Fecha
                          <input
                            type="date"
                            style={baseInputStyle}
                            value={formData.quotation_date || ''}
                            onChange={(event) => onChange('quotation_date', event.target.value)}
                          />
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Días Validez
                          <input
                            type="number"
                            min="0"
                            style={baseInputStyle}
                            value={formData.validity_days || '30'}
                            onChange={(event) => onChange('validity_days', event.target.value)}
                          />
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Ejecutivo
                          <select
                            style={baseInputStyle}
                            value={formData.user_id || ''}
                            onChange={(event) => onChange('user_id', event.target.value)}
                          >
                            {userOptions.map((option) => (
                              <option key={String(option.value)} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {renderFieldError(fieldErrors.user_id)}
                        </label>
                      </div>
                    </div>

                    <div>
                      {sectionTitle('Cliente')}
                      <div style={rowStyle}>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Organización
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                              <input
                                style={{ ...baseInputStyle, width: '100%' }}
                                value={organizationSearch}
                                onFocus={() => setShowOrgOptions(true)}
                                onBlur={() => setTimeout(() => setShowOrgOptions(false), 120)}
                                onChange={(event) => {
                                  setOrganizationSearch(event.target.value);
                                  onChange('organization_id', '');
                                  setShowOrgOptions(true);
                                }}
                                placeholder="Escribe para buscar organización..."
                              />
                              {showOrgOptions && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '36px',
                                    left: 0,
                                    right: 0,
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                    background: colors.panel,
                                    border: `1px solid ${colors.inputBorder}`,
                                    borderRadius: '8px',
                                    zIndex: 10,
                                  }}
                                >
                                  {filteredOrganizations.length > 0 ? (
                                    filteredOrganizations.map((option) => (
                                      <button
                                        key={String(option.value)}
                                        type="button"
                                        onClick={() => {
                                          setOrganizationSearch(option.label);
                                          onChange('organization_id', String(option.value));
                                          setShowOrgOptions(false);
                                        }}
                                        style={{
                                          width: '100%',
                                          textAlign: 'left',
                                          padding: '8px 10px',
                                          border: 'none',
                                          background: 'transparent',
                                          color: colors.text,
                                          cursor: 'pointer',
                                          fontSize: '13px',
                                        }}
                                      >
                                        {option.label}
                                      </button>
                                    ))
                                  ) : (
                                    <div style={{ padding: '8px 10px', color: colors.muted, fontSize: '12px' }}>
                                      Sin coincidencias
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {onAddOrganization ? (
                              <button
                                type="button"
                                onClick={onAddOrganization}
                                title="Nueva organización"
                                aria-label="Nueva organización"
                                style={{
                                  flexShrink: 0,
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '8px',
                                  border: `1px solid ${colors.inputBorder}`,
                                  background: colors.inputBg,
                                  color: colors.text,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                <Plus size={16} />
                              </button>
                            ) : null}
                          </div>
                          {renderFieldError(fieldErrors.organization_id)}
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          RUT
                          <input
                            style={{ ...baseInputStyle, backgroundColor: colors.sectionBg }}
                            value={selectedOrgRut}
                            readOnly
                            placeholder="Se completa al elegir organización"
                          />
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Contacto
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <select
                              style={{ ...baseInputStyle, flex: 1, minWidth: 0 }}
                              value={formData.contact_id || ''}
                              onChange={(event) => onChange('contact_id', event.target.value)}
                              disabled={!formData.organization_id}
                            >
                              {contactOptions.map((option) => (
                                <option key={String(option.value)} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {onAddContact ? (
                              <button
                                type="button"
                                onClick={onAddContact}
                                disabled={!formData.organization_id}
                                title={
                                  formData.organization_id
                                    ? 'Nuevo contacto'
                                    : 'Selecciona una organización primero'
                                }
                                aria-label="Nuevo contacto"
                                style={{
                                  flexShrink: 0,
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '8px',
                                  border: `1px solid ${colors.inputBorder}`,
                                  background: colors.inputBg,
                                  color: colors.text,
                                  cursor: formData.organization_id ? 'pointer' : 'not-allowed',
                                  opacity: formData.organization_id ? 1 : 0.45,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                <Plus size={16} />
                              </button>
                            ) : null}
                          </div>
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Condiciones de pago
                          <select
                            style={baseInputStyle}
                            value={formData.payment_terms || ''}
                            onChange={(event) => onChange('payment_terms', event.target.value)}
                          >
                            {formData.payment_terms &&
                            !paymentTermsOptions.some((option) => option.value === formData.payment_terms) ? (
                              <option value={formData.payment_terms}>{formData.payment_terms}</option>
                            ) : null}
                            {paymentTermsOptions.map((option) => (
                              <option key={option.value || 'empty'} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>

                    <div>
                      {sectionTitle('Entrega')}
                      <div
                        style={{
                          ...rowStyle,
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        }}
                      >
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Días de Entrega
                          <input
                            type="number"
                            min="0"
                            style={baseInputStyle}
                            value={formData.delivery_days || ''}
                            onChange={(event) => onChange('delivery_days', event.target.value)}
                          />
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Opción de Entrega
                          <select
                            style={baseInputStyle}
                            value={formData.delivery_method || 'Despacho'}
                            onChange={(event) => {
                              const nextMethod = event.target.value;
                              onChange('delivery_method', nextMethod);
                              if (nextMethod === 'Retiro en sucursal') {
                                onChange('delivery_place', '');
                              } else if (
                                nextMethod === 'Despacho' &&
                                !String(formData.delivery_place || '').trim()
                              ) {
                                onChange('delivery_place', 'Instalaciones del Cliente');
                              }
                            }}
                          >
                            <option value="Despacho">Despacho</option>
                            <option value="Retiro en sucursal">Retiro en sucursal</option>
                          </select>
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Lugar de Entrega
                          <input
                            style={{
                              ...baseInputStyle,
                              backgroundColor: deliveryPlaceEditable ? colors.inputBg : colors.sectionBg,
                              opacity: deliveryPlaceEditable ? 1 : 0.7,
                              cursor: deliveryPlaceEditable ? 'text' : 'not-allowed',
                            }}
                            value={
                              deliveryPlaceEditable
                                ? formData.delivery_place || 'Instalaciones del Cliente'
                                : ''
                            }
                            onChange={(event) => {
                              if (!deliveryPlaceEditable) return;
                              onChange('delivery_place', event.target.value);
                            }}
                            readOnly={!deliveryPlaceEditable}
                            disabled={!deliveryPlaceEditable}
                            placeholder={
                              deliveryPlaceEditable
                                ? 'Instalaciones del Cliente'
                                : 'No aplica en retiro en sucursal'
                            }
                            title={
                              deliveryPlaceEditable
                                ? undefined
                                : 'En retiro en sucursal el lugar de entrega no aplica'
                            }
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      {sectionTitle('Canal')}
                      <div
                        style={{
                          ...rowStyle,
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          marginBottom: '10px',
                        }}
                      >
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Canal
                          <select
                            style={baseInputStyle}
                            value={formData.channel || ''}
                            onChange={(event) => {
                              const nextChannel = event.target.value;
                              onChange('channel', nextChannel);
                              if (String(nextChannel || '').trim() === 'Directo') {
                                onChange('tender', '');
                              }
                            }}
                          >
                            <option value="">Seleccionar canal…</option>
                            {formData.channel &&
                            !channelOptions.some((option) => option.value === formData.channel) ? (
                              <option value={formData.channel}>{formData.channel}</option>
                            ) : null}
                            {channelOptions.map((option) => (
                              <option key={option.code || option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Licitación
                          <input
                            style={{
                              ...baseInputStyle,
                              backgroundColor: tenderEditable ? colors.inputBg : colors.sectionBg,
                              opacity: tenderEditable ? 1 : 0.7,
                              cursor: tenderEditable ? 'text' : 'not-allowed',
                            }}
                            value={tenderEditable ? formData.tender || '' : ''}
                            onChange={(event) => {
                              if (!tenderEditable) return;
                              onChange('tender', event.target.value);
                            }}
                            readOnly={!tenderEditable}
                            disabled={!tenderEditable}
                            placeholder={
                              tenderEditable
                                ? 'Código o ID de licitación'
                                : channelValue === 'Directo'
                                  ? 'No aplica en canal Directo'
                                  : 'Selecciona un canal para editar la licitación'
                            }
                            title={
                              tenderEditable
                                ? undefined
                                : channelValue === 'Directo'
                                  ? 'En canal Directo la licitación no aplica'
                                  : 'Selecciona un canal distinto a Directo para editar la licitación'
                            }
                          />
                        </label>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        onClick={() => setShowOtherTermsModal(true)}
                        style={{
                          height: '34px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: colors.panel,
                          color: colors.text,
                          cursor: 'pointer',
                          padding: '0 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        Otras Condiciones y Términos
                      </button>
                    </div>

                    <Dialog
                      isOpen={showOtherTermsModal}
                      onClose={() => setShowOtherTermsModal(false)}
                      title="Otras Condiciones y Términos"
                      subtitle="Condiciones adicionales de la cotización"
                      size="sm"
                      isDarkMode={isDarkMode}
                      zIndex={10055}
                    >
                      <div style={{ display: 'grid', gap: '12px', paddingBottom: '4px' }}>
                        <label style={{ color: colors.text, fontSize: '12px' }}>
                          Moneda
                          <select
                            style={baseInputStyle}
                            value={formData.currency || 'CLP'}
                            onChange={(event) => onChange('currency', event.target.value)}
                          >
                            <option value="CLP">CLP</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </label>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => setShowOtherTermsModal(false)}
                            style={{
                              height: '34px',
                              borderRadius: '8px',
                              border: 'none',
                              background: '#2563eb',
                              color: '#fff',
                              cursor: 'pointer',
                              padding: '0 14px',
                              fontSize: '13px',
                              fontWeight: 600,
                            }}
                          >
                            Listo
                          </button>
                        </div>
                      </div>
                    </Dialog>
                  </>
                );
              })()}
            </div>

            <div
              style={{
                background: colors.sectionBg,
                border: `1px solid ${colors.sectionBorder}`,
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: colors.text, fontSize: '13px' }}>Ítems de la cotización</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {onAddUpsellingItem ? (
                    <>
                      <select
                        style={{ ...baseInputStyle, width: '260px' }}
                        value={selectedUpsellingCode}
                        onChange={(event) => setSelectedUpsellingCode(event.target.value)}
                      >
                        <option value="">Catálogo existente</option>
                        {upsellingOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedUpsellingCode) return;
                          onAddUpsellingItem(selectedUpsellingCode);
                          setSelectedUpsellingCode('');
                        }}
                        disabled={!selectedUpsellingCode}
                        title="Agrega a la cotización el producto seleccionado en el catálogo"
                        style={{
                          height: '32px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          cursor: selectedUpsellingCode ? 'pointer' : 'not-allowed',
                          opacity: selectedUpsellingCode ? 1 : 0.55,
                          padding: '0 10px',
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        Usar seleccionado
                      </button>
                      {onCreateUpsellingProduct ? (
                        <button
                          type="button"
                          onClick={() => setShowUpsellingModal(true)}
                          style={{
                            height: '32px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.inputBorder}`,
                            background: isDarkMode ? '#27272a' : '#f3f4f6',
                            color: colors.text,
                            cursor: 'pointer',
                            padding: '0 10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <Plus size={14} /> Agregar upselling
                        </button>
                      ) : null}
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={onAddItem}
                    style={{
                      height: '32px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      padding: '0 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Plus size={14} /> Agregar ítem
                  </button>
                </div>
              </div>

              {onCreateUpsellingProduct && onAddUpsellingItem ? (
                <Dialog
                  isOpen={showUpsellingModal}
                  onClose={() => !upsellingCreateLoading && setShowUpsellingModal(false)}
                  title="Nuevo producto upselling"
                  subtitle="Se guarda en el catálogo (A012) y se agrega como línea en esta cotización."
                  size="md"
                  isDarkMode={isDarkMode}
                  zIndex={10050}
                  closeOnOverlayClick={!upsellingCreateLoading}
                  closeOnEscape={!upsellingCreateLoading}
                >
                  <div style={{ display: 'grid', gap: '14px' }}>
                    <label style={{ color: colors.text, fontSize: '13px', display: 'grid', gap: '6px' }}>
                      Nombre del producto
                      <input
                        style={baseInputStyle}
                        value={newUpsellingName}
                        onChange={(e) => setNewUpsellingName(e.target.value)}
                        placeholder="Ej. Rodillo sintético 23 cm"
                        disabled={upsellingCreateLoading}
                      />
                    </label>
                    <label style={{ color: colors.text, fontSize: '13px', display: 'grid', gap: '6px' }}>
                      Precio base (opcional)
                      <input
                        type="number"
                        min={0}
                        step="any"
                        style={baseInputStyle}
                        value={newUpsellingPrice}
                        onChange={(e) => setNewUpsellingPrice(e.target.value)}
                        placeholder="0"
                        disabled={upsellingCreateLoading}
                      />
                    </label>
                    <div>
                      <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: colors.text }}>
                        Categoría
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: colors.text,
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="up-cat"
                            checked={upsellingCategoryMode === 'auto'}
                            onChange={() => setUpsellingCategoryMode('auto')}
                            disabled={upsellingCreateLoading}
                          />
                          Automática (según palabras del nombre: brocha, rodillo, diluyente, etc.)
                        </label>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: colors.text,
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="up-cat"
                            checked={upsellingCategoryMode === 'manual'}
                            onChange={() => setUpsellingCategoryMode('manual')}
                            disabled={upsellingCreateLoading}
                          />
                          Elegir manualmente
                        </label>
                      </div>
                      {upsellingCategoryMode === 'manual' ? (
                        <select
                          style={{ ...baseInputStyle, marginTop: '10px' }}
                          value={manualUpsellingCategoryCode}
                          onChange={(e) => setManualUpsellingCategoryCode(e.target.value)}
                          disabled={upsellingCreateLoading}
                        >
                          <option value="">Seleccionar categoría</option>
                          {(upsellingCategoryOptions || []).map((row) => (
                            <option key={row.code} value={row.code}>
                              {row.code} — {row.name}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                    {upsellingCreateError ? (
                      <p style={{ margin: 0, fontSize: '12px', color: colors.danger }}>{upsellingCreateError}</p>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                      <button
                        type="button"
                        disabled={upsellingCreateLoading}
                        onClick={() => setShowUpsellingModal(false)}
                        style={{
                          height: '36px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          padding: '0 14px',
                          cursor: upsellingCreateLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        disabled={upsellingCreateLoading}
                        onClick={async () => {
                          setUpsellingCreateError('');
                          const name = newUpsellingName.trim();
                          if (!name) {
                            setUpsellingCreateError('Indica el nombre del producto.');
                            return;
                          }
                          if (upsellingCategoryMode === 'manual' && !manualUpsellingCategoryCode) {
                            setUpsellingCreateError('Selecciona una categoría o usa el modo automático.');
                            return;
                          }
                          setUpsellingCreateLoading(true);
                          try {
                            const created = await onCreateUpsellingProduct({
                              product_name: name,
                              base_price: newUpsellingPrice,
                              category_code:
                                upsellingCategoryMode === 'manual' ? manualUpsellingCategoryCode : '',
                            });
                            setShowUpsellingModal(false);
                            if (created && created.upselling_code) {
                              onAddUpsellingItem(String(created.upselling_code));
                            }
                          } catch (err) {
                            setUpsellingCreateError(err?.message || 'No se pudo guardar el producto.');
                          } finally {
                            setUpsellingCreateLoading(false);
                          }
                        }}
                        style={{
                          height: '36px',
                          borderRadius: '8px',
                          border: 'none',
                          background: upsellingCreateLoading ? colors.muted : '#2563eb',
                          color: '#fff',
                          padding: '0 16px',
                          fontWeight: 600,
                          cursor: upsellingCreateLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {upsellingCreateLoading ? 'Guardando…' : 'Guardar y agregar a la cotización'}
                      </button>
                    </div>
                  </div>
                </Dialog>
              ) : null}

              {fieldErrors.itemsGeneral || livePriceRangeCount > 0 ? (
                <p
                  style={{
                    color:
                      fieldErrors.itemsGeneral && !isFieldWarningMessage(fieldErrors.itemsGeneral)
                        ? colors.danger
                        : '#b45309',
                    fontSize: '12px',
                    margin: '0 0 10px',
                    lineHeight: 1.35,
                  }}
                >
                  {fieldErrors.itemsGeneral ||
                    `Advertencia: ${livePriceRangeCount} ítem${
                      livePriceRangeCount === 1 ? '' : 's'
                    } con precio neto fuera del rango mínimo/máximo del formato.`}
                </p>
              ) : null}

              {quotationItems.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '5fr 120px 96px 170px 76px 118px 76px 104px 110px 72px',
                    gap: '8px',
                    alignItems: 'center',
                    marginBottom: '6px',
                    padding: '0 2px',
                  }}
                >
                  {[
                    'Producto',
                    'Código',
                    'Cod. color',
                    'Nombre color',
                    'Cant.',
                    'Formato',
                    'Desc %',
                    'Precio Unit. Neto',
                    'Total neto',
                    '',
                  ].map((label) => (
                    <span
                      key={label || 'actions'}
                      style={{
                        color: colors.muted,
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: 1.2,
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}

              {quotationItems.map((item, index) => {
                const rowErr = fieldErrors.items?.[index] || {};
                const liveUnitPriceWarning =
                  typeof getUnitPriceWarning === 'function' ? getUnitPriceWarning(item) : '';
                const unitPriceMessage = rowErr.unit_price || liveUnitPriceWarning || '';
                const rowKey = quotationItemRowKey(item, index);
                const catalogLabel = quotationItemCatalogLabel(item, itemDescriptionOptions);
                const descriptionValue =
                  openDescriptionKey === rowKey
                    ? itemDescriptionSearch[rowKey] ?? catalogLabel
                    : catalogLabel;
                const rowQty = Number(item.quantity || 0);
                const rowUnitPrice = Number(item.unit_price || 0);
                const rowDiscount = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
                const rowNetTotal = Math.round(
                  rowQty * rowUnitPrice * (1 - rowDiscount / 100)
                );
                return (
                <div
                  key={rowKey}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '5fr 120px 96px 170px 76px 118px 76px 104px 110px 72px',
                    gap: '8px',
                    alignItems: 'start',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ position: 'relative' }}>
                      <input
                        style={baseInputStyle}
                        value={descriptionValue}
                        onFocus={() => {
                          setItemDescriptionSearch((prev) => ({
                            ...prev,
                            [rowKey]: prev[rowKey] ?? catalogLabel,
                          }));
                          setOpenDescriptionKey(rowKey);
                        }}
                        onBlur={() => setTimeout(() => setOpenDescriptionKey((prev) => (prev === rowKey ? null : prev)), 120)}
                        onChange={(event) => {
                          const typedValue = event.target.value;
                          setItemDescriptionSearch((prev) => ({ ...prev, [rowKey]: typedValue }));
                          setOpenDescriptionKey(rowKey);
                        }}
                        placeholder="Busca por nombre o código y selecciona..."
                      />
                      {openDescriptionKey === rowKey ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: '36px',
                            left: 0,
                            right: 0,
                            maxHeight: '180px',
                            overflowY: 'auto',
                            background: colors.panel,
                            border: `1px solid ${colors.inputBorder}`,
                            borderRadius: '8px',
                            zIndex: 10,
                          }}
                        >
                          {itemDescriptionOptions
                            .filter((option) => option.value)
                            .filter((option) => {
                              const query = String(itemDescriptionSearch[rowKey] ?? catalogLabel)
                                .trim()
                                .toLowerCase();
                              if (!query) return true;
                              const label = String(option.label || '').toLowerCase();
                              const val = String(option.value || '').toLowerCase();
                              return label.includes(query) || val.includes(query);
                            })
                            .slice(0, 20)
                            .map((option) => (
                              <button
                                key={`${option.kind || 'cat'}-${option.value}`}
                                type="button"
                                onClick={() => {
                                  setItemDescriptionSearch((prev) => ({
                                    ...prev,
                                    [rowKey]: quotationItemOptionLabel(option),
                                  }));
                                  onItemChange(index, 'product_sku', option.value);
                                  setOpenDescriptionKey(null);
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  border: 'none',
                                  background: 'transparent',
                                  color: colors.text,
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                }}
                              >
                                {quotationItemOptionLabel(option)}
                              </button>
                            ))}
                        </div>
                      ) : null}
                    {renderFieldError(rowErr.item_name)}
                  </div>
                  <input
                      style={{ ...baseInputStyle, backgroundColor: colors.sectionBg }}
                      value={String(item.product_sku || '').trim() || buildItemSku(item)}
                      readOnly
                      placeholder="Código de catálogo"
                    />
                  <input
                      style={{ ...baseInputStyle, backgroundColor: colors.sectionBg }}
                      value={item.color_code || ''}
                      readOnly
                      placeholder="0000"
                    />
                  <div style={{ position: 'relative' }}>
                      <input
                        style={baseInputStyle}
                        value={openColorKey === rowKey ? itemColorSearch[rowKey] ?? item.color ?? '' : item.color || ''}
                        onFocus={() => {
                          setItemColorSearch((prev) => ({
                            ...prev,
                            [rowKey]: prev[rowKey] ?? item.color ?? '',
                          }));
                          setOpenColorKey(rowKey);
                        }}
                        onBlur={() => setTimeout(() => setOpenColorKey((prev) => (prev === rowKey ? null : prev)), 120)}
                        onChange={(event) => {
                          const typedValue = event.target.value;
                          setItemColorSearch((prev) => ({ ...prev, [rowKey]: typedValue }));
                          onItemChange(index, 'color', typedValue);
                          if (!typedValue.trim()) {
                            onItemChange(index, 'color_code', '');
                          }
                          setOpenColorKey(rowKey);
                        }}
                        placeholder="Escribe para buscar color..."
                      />
                      {openColorKey === rowKey ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: '36px',
                            left: 0,
                            right: 0,
                            maxHeight: '180px',
                            overflowY: 'auto',
                            background: colors.panel,
                            border: `1px solid ${colors.inputBorder}`,
                            borderRadius: '8px',
                            zIndex: 10,
                          }}
                        >
                          {itemColorOptions
                            .filter((option) => option.value)
                            .filter((option) => {
                              const query = String(itemColorSearch[rowKey] ?? item.color ?? '').trim().toLowerCase();
                              if (!query) return true;
                              return option.label.toLowerCase().includes(query);
                            })
                            .slice(0, 20)
                            .map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  setItemColorSearch((prev) => ({ ...prev, [rowKey]: option.value }));
                                  onItemChange(index, 'color', option.value);
                                  onItemChange(index, 'color_code', option.code || '');
                                  setOpenColorKey(null);
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  border: 'none',
                                  background: 'transparent',
                                  color: colors.text,
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                        </div>
                      ) : null}
                  </div>
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      style={baseInputStyle}
                      value={item.quantity || ''}
                      onChange={(event) => onItemChange(index, 'quantity', event.target.value)}
                    />
                    {renderFieldError(rowErr.quantity)}
                  </div>
                  <select
                      style={baseInputStyle}
                      value={item.unit || ''}
                      onChange={(event) => onItemChange(index, 'unit', event.target.value)}
                    >
                      {item.unit && !itemFormatOptions.some((option) => option.value === item.unit) ? (
                        <option value={item.unit}>{item.unit}</option>
                      ) : null}
                      {itemFormatOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  <input
                      type="number"
                      step="0.01"
                      style={baseInputStyle}
                      value={item.discount_percent || ''}
                      onChange={(event) => onItemChange(index, 'discount_percent', event.target.value)}
                    />
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      style={{
                        ...baseInputStyle,
                        ...(unitPriceMessage && isFieldWarningMessage(unitPriceMessage)
                          ? { borderColor: '#f59e0b' }
                          : null),
                        ...(rowErr.unit_price && !isFieldWarningMessage(rowErr.unit_price)
                          ? { borderColor: colors.danger }
                          : null),
                      }}
                      value={item.unit_price || ''}
                      onChange={(event) => onItemChange(index, 'unit_price', event.target.value)}
                    />
                    {renderFieldError(unitPriceMessage)}
                  </div>
                  <div
                    style={{
                      ...baseInputStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      backgroundColor: colors.sectionBg,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={formatMoneyClp(rowNetTotal)}
                  >
                    {formatMoneyClp(rowNetTotal)}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                    {onDuplicateItem ? (
                      <button
                        type="button"
                        onClick={() => onDuplicateItem(index)}
                        style={{
                          height: '34px',
                          width: '34px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                        }}
                        aria-label="Duplicar ítem"
                        title="Duplicar ítem"
                      >
                        <Copy size={14} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onRemoveItem(index)}
                      style={{
                        height: '34px',
                        width: '34px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.inputBorder}`,
                        background: 'transparent',
                        color: colors.danger,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                      }}
                      aria-label="Eliminar ítem"
                      title="Eliminar ítem"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                );
              })}

              <div
                style={{
                  marginTop: '12px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <div
                  style={{
                    minWidth: '240px',
                    display: 'grid',
                    gap: '6px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.sectionBorder}`,
                    background: colors.panel,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '13px', color: colors.text }}>
                    <span>Total neto</span>
                    <span style={{ fontWeight: 600 }}>{formatMoneyClp(netTotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '13px', color: colors.text }}>
                    <span>IVA (19%)</span>
                    <span style={{ fontWeight: 600 }}>{formatMoneyClp(ivaTotal)}</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '24px',
                      fontSize: '13px',
                      color: colors.text,
                      paddingTop: '6px',
                      borderTop: `1px solid ${colors.sectionBorder}`,
                      fontWeight: 700,
                    }}
                  >
                    <span>Total + IVA</span>
                    <span>{formatMoneyClp(grossTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: colors.sectionBg,
                border: `1px solid ${colors.sectionBorder}`,
                borderRadius: '10px',
                padding: '12px',
                marginTop: '12px',
              }}
            >
              <h4 style={{ margin: '0 0 10px', color: colors.text, fontSize: '13px' }}>
                Imágenes en el PDF
              </h4>
              <p style={{ margin: '0 0 10px', color: colors.muted, fontSize: '12px' }}>
                Configura si el PDF emitido muestra fotos de los productos de pintura cotizados (sobre comentarios).
              </p>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px',
                  fontSize: '13px',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.pdf_include_product_images !== false}
                  onChange={(event) => onChange('pdf_include_product_images', event.target.checked)}
                />
                Incluir imágenes de productos en el PDF
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: colors.muted }}>
                  Tamaño de las imágenes
                </span>
                <select
                  style={baseInputStyle}
                  value={formData.pdf_product_image_size_mm || '48'}
                  disabled={formData.pdf_include_product_images === false}
                  onChange={(event) => onChange('pdf_product_image_size_mm', event.target.value)}
                >
                  {pdfProductImageSizeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div
              style={{
                background: colors.sectionBg,
                border: `1px solid ${colors.sectionBorder}`,
                borderRadius: '10px',
                padding: '12px',
                marginTop: '12px',
              }}
            >
              <h4 style={{ margin: '0 0 10px', color: colors.text, fontSize: '13px' }}>Notas compartidas</h4>
              <p style={{ margin: '0 0 8px', color: colors.muted, fontSize: '12px' }}>
                Visibles también en la venta y entregas asociadas. Un comentario por línea (se guarda separado por
                ;).
              </p>
              <textarea
                style={baseTextareaStyle}
                value={formData.comments_text || ''}
                onChange={(event) => onChange('comments_text', event.target.value)}
                placeholder="Ej: Requiere certificado técnico&#10;Entrega parcial permitida"
              />
            </div>

            <div
              style={{
                background: colors.sectionBg,
                border: `1px solid ${colors.sectionBorder}`,
                borderRadius: '10px',
                padding: '12px',
                marginTop: '12px',
              }}
            >
              <h4 style={{ margin: '0 0 10px', color: colors.text, fontSize: '13px' }}>Observaciones internas</h4>
              <p style={{ margin: '0 0 8px', color: colors.muted, fontSize: '12px' }}>
                Compartidas con venta/entregas. No se muestran en el PDF emitido al cliente.
              </p>
              <textarea
                style={baseTextareaStyle}
                value={formData.observations_text || ''}
                onChange={(event) => onChange('observations_text', event.target.value)}
                placeholder="Ej: Cliente prefiere coordinar entrega con 48h de anticipación."
              />
            </div>

            {formError ? (
              <p style={{ color: colors.danger, margin: '10px 2px 0', fontSize: '12px' }}>{formError}</p>
            ) : null}
          </div>

          <div
            style={{
              borderTop: `1px solid ${colors.panelBorder}`,
              padding: '12px 18px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              style={{
                height: '34px',
                borderRadius: '8px',
                padding: '0 12px',
                border: `1px solid ${colors.inputBorder}`,
                backgroundColor: 'transparent',
                color: colors.text,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onEmit}
              disabled={isSaving}
              style={{
                height: '34px',
                borderRadius: '8px',
                padding: '0 12px',
                border: `1px solid ${colors.inputBorder}`,
                backgroundColor: '#0f766e',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Emitir
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              style={{
                height: '34px',
                borderRadius: '8px',
                padding: '0 12px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    ) : null
  );
};

export default QuotationsForm;
